import React from 'react'
import { useHistory } from 'react-router-dom'
import OllieIcon from 'src/components/icons/OllieIcon'
import styled from 'styled-components/macro'
import GrowthEngineHumanDemoRow from 'src/components/growthEngine/GrowthEngineHumanDemoRow'
import { OLLIE_PLAN_LIMITS } from 'src/constants/olliePlanFeatures'
import { planDisplayName } from 'src/constants/planDisplay'

function getUsageLabel(usage, limitType) {
  if (!usage) {
    return null
  }

  if (limitType === 'daily' && usage.dailyLimit != null) {
    return `${usage.dailyUsed} of ${usage.dailyLimit} questions used today`
  }

  if (usage.monthlyLimit != null) {
    return `${usage.monthlyUsed} of ${usage.monthlyLimit} questions used this month`
  }

  return null
}

const LITE_NAME = planDisplayName('Free')

export const OLLIE_PAYWALL_DEFAULT_TITLE = 'Keep getting answers from Ollie'

// Literal on purpose: app/utils/ollieOrderingAccess.js and the parity test compare this
// text. Numbers mirror OLLIE_PLAN_LIMITS (free 20/month, 5/day; paid 200/month).
export const OLLIE_PAYWALL_DEFAULT_BODY =
  'Ollie answers questions about your orders, costs, and how to use OMS. OMS Lite includes 20 questions a month (5 a day). Starter, Growth, and Pro include 200 a month.'

const PAID_BENEFITS = [
  `${OLLIE_PLAN_LIMITS.paidMonthly} Ollie questions a month, up from ${OLLIE_PLAN_LIMITS.freeMonthly} on ${LITE_NAME}`,
  'No daily question cap',
  'More OMS tools alongside Ollie on Starter, Growth, or Pro',
]

const OlliePaywallModal = ({
  visible,
  paywall,
  usage,
  limitType,
  venueId,
  onClose,
}) => {
  const history = useHistory()

  if (!visible) {
    return null
  }

  const title = paywall?.title || OLLIE_PAYWALL_DEFAULT_TITLE
  const body = paywall?.body || OLLIE_PAYWALL_DEFAULT_BODY
  const ctaLabel = paywall?.ctaLabel || 'View plans'
  const ctaPath = paywall?.ctaPath || `/v/${venueId}/subscriptions/oms-plans`
  const usageLabel = getUsageLabel(usage, limitType)
  const limitBadge =
    limitType === 'daily' ? 'Daily limit reached' : 'Monthly limit reached'

  const handleUpgrade = () => {
    history.push(ctaPath)
    onClose()
  }

  return (
    <Overlay role='presentation' onClick={onClose}>
      <Dialog
        role='dialog'
        aria-modal='true'
        aria-labelledby='ollie-paywall-title'
        data-testid='ollie-paywall'
        onClick={event => event.stopPropagation()}
      >
        <AccentBar />
        <Content>
          <IconWrap>
            <OllieIcon size={48} />
          </IconWrap>

          <Badge>{limitBadge}</Badge>
          <Title id='ollie-paywall-title'>{title}</Title>
          <Body>{body}</Body>

          {usageLabel && <UsagePill>{usageLabel}</UsagePill>}

          <BenefitsList>
            {PAID_BENEFITS.map(benefit => (
              <BenefitItem key={benefit}>{benefit}</BenefitItem>
            ))}
          </BenefitsList>

          <Actions>
            <PrimaryButton type='button' onClick={handleUpgrade}>
              {ctaLabel}
            </PrimaryButton>
            <SecondaryButton type='button' onClick={onClose}>
              Not now
            </SecondaryButton>
          </Actions>

          <GrowthEngineHumanDemoRow align='center' />
        </Content>
      </Dialog>
    </Overlay>
  )
}

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1200;
`

const Dialog = styled.div`
  position: relative;
  width: 100%;
  max-width: 460px;
  background: ${({ theme }) => theme.colours.mayoWhite};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.2);
`

const AccentBar = styled.div`
  height: 6px;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colours.lettuceGreen},
    ${({ theme }) => theme.colours.avocadoGreen}
  );
`

const Content = styled.div`
  padding: 28px 28px 24px;
  text-align: center;
`

const IconWrap = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 12px;
`

const Badge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colours.bgGrey};
  color: ${({ theme }) => theme.colours.bbqBlack};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 12px;
`

const Title = styled.h2`
  margin: 0 0 12px;
  font-size: 24px;
  line-height: 1.25;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const Body = styled.p`
  margin: 0 0 16px;
  font-size: 15px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colours.bbqBlack};
`

const UsagePill = styled.div`
  display: inline-block;
  margin-bottom: 18px;
  padding: 8px 14px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colours.bgGrey};
  color: ${({ theme }) => theme.colours.bbqBlack};
  font-size: 14px;
  font-weight: 600;
`

const BenefitsList = styled.ul`
  list-style: none;
  margin: 0 0 24px;
  padding: 16px;
  text-align: left;
  background: ${({ theme }) => theme.colours.bgGrey};
  border-radius: 12px;
`

const BenefitItem = styled.li`
  position: relative;
  padding-left: 22px;
  font-size: 14px;
  line-height: 1.45;
  color: ${({ theme }) => theme.colours.bbqBlack};

  &:not(:last-child) {
    margin-bottom: 10px;
  }

  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: ${({ theme }) => theme.colours.lettuceGreen};
    font-weight: 700;
  }
`

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const PrimaryButton = styled.button`
  border: none;
  border-radius: 10px;
  padding: 14px 20px;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  background: ${({ theme }) => theme.colours.lettuceGreen};
  color: ${({ theme }) => theme.colours.bbqBlack};

  &:hover {
    background: ${({ theme }) => theme.colours.avocadoGreen};
  }
`

const SecondaryButton = styled.button`
  border: none;
  background: transparent;
  padding: 8px;
  font-size: 14px;
  color: ${({ theme }) => theme.colours.oliveGrey};
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colours.bbqBlack};
  }
`

export default OlliePaywallModal
export { getUsageLabel }
